// src/pages/employees/Employees.jsx

import React, { useState, useEffect } from 'react';
import { Users, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import { employeeService } from '../../services/employeeService';
import { authService } from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import StatusBadge from '../../components/StatusBadge';

const ROLE_LABELS = {
  hr_manager: 'HR Manager',
  finance_manager: 'Finance Manager',
  sales_manager: 'Sales Manager',
  sales_rep: 'Sales Representative'
};

const Employees = () => {
  const { user } = useAuth();
  const isSuperAdmin = user && user.role === 'super_admin';

  const [activeTab, setActiveTab] = useState('employees');
  const [employees, setEmployees] = useState([]);
  const [managers, setManagers] = useState([]);
  const [managersLoading, setManagersLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const loadEmployees = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await employeeService.getEmployees(undefined, undefined, search || undefined);
      setEmployees(res.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = async () => {
    setManagersLoading(true);
    try {
      const res = await authService.getManagersAndReps();
      setManagers(res.users || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setManagersLoading(false);
    }
  };

  useEffect(() => {
    loadEmployees();
  }, []);

  useEffect(() => {
    if (activeTab === 'managers' && managers.length === 0) loadManagers();
  }, [activeTab]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    loadEmployees();
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Employees</h1>
          <p className="text-sm text-gray-500 mt-0.5">Employee directory</p>
        </div>
      </div>

      <div className="flex items-center gap-1 mb-5 border-b border-gray-100">
        <button
          onClick={() => setActiveTab('employees')}
          className={
            'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ' +
            (activeTab === 'employees'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700')
          }
        >
          Employees
        </button>
        <button
          onClick={() => setActiveTab('managers')}
          className={
            'px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ' +
            (activeTab === 'managers'
              ? 'border-primary text-primary'
              : 'border-transparent text-gray-500 hover:text-gray-700')
          }
        >
          Managers &amp; Reps
        </button>
      </div>

      {error && (
        <div className="mb-4 px-4 py-2.5 rounded-control bg-red-50 text-red-700 text-sm">{error}</div>
      )}

      {activeTab === 'employees' && (
        <div>
          <form onSubmit={handleSearchSubmit} className="mb-4 flex gap-2">
            <input
              type="text"
              placeholder="Search by name or employee ID..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1 px-3 py-2.5 rounded-control border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-light"
            />
            <button
              type="submit"
              className="px-4 py-2.5 rounded-control border border-gray-200 text-sm font-medium text-gray-600 hover:bg-surface-subtle"
            >
              Search
            </button>
          </form>

          {loading && (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}

          {!loading && employees.length === 0 && (
            <div className="bg-white rounded-card shadow-soft border border-gray-100 p-12 text-center">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No employees found.</p>
            </div>
          )}

          {!loading && employees.length > 0 && (
            <div>
              <div className="hidden md:block bg-white rounded-card shadow-soft border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">Employee ID</th>
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Department</th>
                      <th className="px-5 py-3">Designation</th>
                      <th className="px-5 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employees.map((e) => (
                      <tr key={e.id} className="border-b border-gray-50 last:border-0 hover:bg-surface-subtle transition-colors">
                        <td className="px-5 py-3.5 text-gray-600">{e.employee_id}</td>
                        <td className="px-5 py-3.5 font-medium text-gray-800">
                          <Link to={'/employees/' + e.employee_id} className="hover:text-primary-light hover:underline">
                            {e.full_name}
                          </Link>
                        </td>
                        <td className="px-5 py-3.5 text-gray-600">{e.department}</td>
                        <td className="px-5 py-3.5 text-gray-600">{e.designation}</td>
                        <td className="px-5 py-3.5"><StatusBadge status={e.status} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3">
                {employees.map((e) => (
                  <div key={e.id} className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <Link to={'/employees/' + e.employee_id} className="font-medium text-gray-800 text-sm hover:text-primary-light hover:underline">
                        {e.full_name}
                      </Link>
                      <StatusBadge status={e.status} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{e.employee_id}</p>
                    <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
                      <span>{e.department}</span>
                      <span>{e.designation}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'managers' && (
        <div>
          {managersLoading && (
            <div className="flex items-center justify-center py-20 text-gray-400">
              <Loader2 className="w-6 h-6 animate-spin" />
            </div>
          )}
          {!managersLoading && managers.length === 0 && (
            <div className="bg-white rounded-card shadow-soft border border-gray-100 p-12 text-center">
              <Users className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-sm text-gray-500">No managers or reps invited yet.</p>
            </div>
          )}
          {!managersLoading && managers.length > 0 && (
            <div>
              <div className="hidden md:block bg-white rounded-card shadow-soft border border-gray-100 overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-100 text-left text-xs font-medium text-gray-400 uppercase tracking-wide">
                      <th className="px-5 py-3">Name</th>
                      <th className="px-5 py-3">Email</th>
                      <th className="px-5 py-3">Role</th>
                      <th className="px-5 py-3">Account Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managers.map((m) => (
                      <tr key={m.id} className="border-b border-gray-50 last:border-0 hover:bg-surface-subtle transition-colors">
                        <td className="px-5 py-3.5 font-medium text-gray-800">{m.full_name}</td>
                        <td className="px-5 py-3.5 text-gray-600">{m.email}</td>
                        <td className="px-5 py-3.5 text-gray-600">{ROLE_LABELS[m.role] || m.role}</td>
                        <td className="px-5 py-3.5"><StatusBadge status={m.is_active ? 'Active' : 'Pending'} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="md:hidden space-y-3">
                {managers.map((m) => (
                  <div key={m.id} className="bg-white rounded-card shadow-soft border border-gray-100 p-4">
                    <div className="flex items-start justify-between gap-2">
                      <p className="font-medium text-gray-800 text-sm">{m.full_name}</p>
                      <StatusBadge status={m.is_active ? 'Active' : 'Pending'} />
                    </div>
                    <p className="text-xs text-gray-500 mt-0.5">{m.email}</p>
                    <p className="text-xs text-gray-500 mt-1">{ROLE_LABELS[m.role] || m.role}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Employees;